import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Typography, 
  Box,
  Grid,
  Button,
  Card,
  LinearProgress,
  Alert,
  Paper,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Checkbox,
  IconButton,
  useTheme,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Grow,
  Zoom,
  Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { 
  generateExam, 
  checkGenerationStatus, 
  downloadExam, 
  fetchAvailableTopics,
  fetchAvailableMarks,
  fetchCourses,
  fetchSubscriptionStatus
} from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useAnalytics, useComponentTracking } from '../hooks/useAnalytics';
import SubscribeButton from '../components/SubscribeButton';
import SubscribeButtonWithCoupon from '../components/SubscribeButtonWithCoupon';
import AlphaReferralCardCompact from '../components/AlphaReferralCardCompact';
import analytics from '../services/analytics';

const TopicTree = ({ 
  topics, 
  selectedTopics, 
  onSelectionChange, 
  expansionState,
  onToggleExpand,
  onFoldAll,
  onUnfoldAll
}) => {
  const theme = useTheme();
  const selectedSet = new Set(selectedTopics);
  const [searchTerm, setSearchTerm] = useState('');

  const getAllNodePaths = useCallback((node, currentPath = '', paths = new Set()) => {
    if (!node) return paths;

    if (Array.isArray(node)) {
    } else if (typeof node === 'object') {
      Object.keys(node).forEach(name => {
        const childPath = currentPath ? `${currentPath}/${name}` : name;
        paths.add(childPath);
        getAllNodePaths(node[name], childPath, paths);
      });
    }
    return paths;
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const { filteredTopics, expansionUpdates } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const newExpansionUpdates = {};

    function filterNodes(node, currentPath = '') {
      if (!node) return null;

      if (Array.isArray(node)) {
        const matchingLeaves = node.filter(leaf => leaf.toLowerCase().includes(term));
        return matchingLeaves.length > 0 ? matchingLeaves : null;
      }

      if (typeof node === 'object') {
        const filteredChildren = {};
        let hasMatch = false;

        Object.entries(node).forEach(([key, child]) => {
          const childPath = currentPath ? `${currentPath}/${key}` : key;
          const keyMatches = key.toLowerCase().includes(term);
          const filteredChild = filterNodes(child, childPath);

          if (keyMatches || filteredChild !== null) {
            filteredChildren[key] = child;
            hasMatch = true;
            if (term && !expansionState[childPath]) {
              newExpansionUpdates[childPath] = true;
            }
          }
        });

        return hasMatch ? filteredChildren : null;
      }

      return null;
    }

    const filtered = term ? filterNodes(topics) : topics;
    return { filteredTopics: filtered || {}, expansionUpdates: newExpansionUpdates };
  }, [searchTerm, topics, expansionState]);

  useEffect(() => {
    if (Object.keys(expansionUpdates).length > 0) {
      Object.entries(expansionUpdates).forEach(([path, shouldExpand]) => {
        if (shouldExpand && !expansionState[path]) {
          onToggleExpand(path);
        }
      });
    }
  }, [expansionUpdates, expansionState, onToggleExpand]);

  const renderTree = useCallback((node, path = '', depth = 0) => {
    if (!node) return null;

    if (Array.isArray(node)) {
      return node.map((leaf) => {
        const leafPath = `${path}/${leaf}`;
        const isLeafSelected = selectedSet.has(leafPath);
        return (
          <ListItem
            key={leafPath}
            sx={{
              pl: 4 + depth * 2,
              py: 0,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
              }
            }}
            component="div"
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Checkbox
                edge="start"
                checked={isLeafSelected}
                onChange={() => onSelectionChange(leafPath, !isLeafSelected, [])}
                tabIndex={-1}
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={leaf}
              primaryTypographyProps={{
                variant: 'body2'
              }}
              sx={{ ml: 1.5 }}
            />
          </ListItem>
        );
      });
    }

    const entries = Object.entries(node);
    const checkChildrenSelected = (children, currentPath) => {
      const childPaths = getAllNodePaths(children, currentPath);
      return Array.from(childPaths).some(childPath => selectedSet.has(childPath));
    };

    
    const getAllDescendantPaths = (node, currentPath) => {
        let paths = [];
        if (!node) return paths;
        if (Array.isArray(node)) {
            node.forEach(leaf => { paths.push(`${currentPath}/${leaf}`); });
        } else if (typeof node === 'object') {
            Object.entries(node).forEach(([childName, childData]) => {
                const childPath = currentPath ? `${currentPath}/${childName}` : childName;
                paths.push(childPath);
                paths = paths.concat(getAllDescendantPaths(childData, childPath));
            });
        }
        return paths;
    };

    return entries.map(([name, children]) => {
      const currentPath = path ? `${path}/${name}` : name;
      const isExpanded = expansionState[currentPath] || false;
      // Check if this node has actual children (not empty array or object)
      const hasChildren = children && (
        (Array.isArray(children) && children.length > 0) || 
        (typeof children === 'object' && Object.keys(children).length > 0)
      );
      const isFolder = !Array.isArray(children) && typeof children === 'object' && hasChildren;
      const hasChildrenSelected = checkChildrenSelected(children, currentPath);
      const isExactlySelected = selectedSet.has(currentPath);
      const isSelected = isExactlySelected || hasChildrenSelected;
      const isPartiallySelected = hasChildrenSelected && !isExactlySelected;

      // Get background color based on depth like V2
      const bgColor = depth === 0
        ? alpha(theme.palette.primary.main, 0.08)
        : depth === 1
          ? alpha(theme.palette.secondary.main, 0.05)
          : 'transparent';

      return (
        <React.Fragment key={currentPath}>
          <ListItem
            onClick={() => {
              if (hasChildren) {
                onToggleExpand(currentPath);
              } else {
                const descendantPaths = getAllDescendantPaths(children, currentPath);
                onSelectionChange(currentPath, !isSelected, descendantPaths);
              }
            }}
            sx={{
              pl: 2 + depth * 2,
              py: 0.2,
              cursor: 'pointer',
              borderLeft: depth === 0
                ? `4px solid ${theme.palette.primary.main}`
                : depth === 1
                  ? `4px solid ${theme.palette.secondary.main}`
                  : 'none',
              backgroundColor: bgColor,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
              },
              borderRadius: '4px',
              mb: 0.5,
              transition: 'background-color 0.2s',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Checkbox
                edge="start"
                checked={isSelected}
                indeterminate={isPartiallySelected}
                onChange={(event) => {
                  event.stopPropagation();
                  const descendantPaths = getAllDescendantPaths(children, currentPath);
                  onSelectionChange(currentPath, !isSelected, descendantPaths);
                }}
                tabIndex={-1}
                size="small"
                color="primary"
              />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 36, ml: -2 }}>
              {hasChildren ? (
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); onToggleExpand(currentPath); }}
                  aria-label={isExpanded ? "collapse" : "expand"}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded
                    ? <ExpandLess /> 
                    : <ExpandMoreIcon />}
                </IconButton>
              ) : (
                 <Box sx={{ width: 24 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={name}
              primaryTypographyProps={{
                variant: depth === 0 ? 'body1' : 'body2',
                sx: {
                  fontWeight: depth === 0 ? 500 : 400,
                  ml: -1
                }
              }}
            />
          </ListItem>
          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderTree(children, currentPath, depth + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  }, [selectedTopics, selectedSet, expansionState, onSelectionChange, onToggleExpand, getAllNodePaths, theme]);

  return (
    <Paper elevation={2} sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: 3, 
        borderRadius: 2, 
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4]
        },
        overflow: 'auto',
        position: 'relative'
      }}>
        <Box sx={{ 
          position: 'absolute', 
          top: 16, 
          left: 16,
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: 'white',
          color: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 500,
          fontSize: '1.1rem',
          zIndex: 1,
          border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          2
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pl: 5 }}>
          <Box sx={{ flex: 1 }} />
          {selectedTopics.length > 0 && (
            <Fade in={true} timeout={300}>
              <Chip 
                label={`${selectedTopics.length} selected`}
                color="primary"
                size="small"
              />
            </Fade>
          )}
        </Box>
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search topics..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Expand All">
            <IconButton size="small" onClick={onUnfoldAll}>
              <UnfoldMoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton size="small" onClick={onFoldAll}>
              <UnfoldLessIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
        <List dense>
          {renderTree(filteredTopics)}
        </List>
      </Box>
      {selectedTopics.length > 0 && (
        <Button 
          size="small" 
          onClick={() => onSelectionChange('', false, selectedTopics)}
          sx={{ mt: 1 }}
        >
          Clear all selections
        </Button>
      )}
      </Paper>
  );
};

const GenerateExam = () => {
  const { user } = useAuth();
  const { track, trackExamGeneration, trackError } = useAnalytics();
  const theme = useTheme();
  useComponentTracking('GenerateExam');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [difficulty, setDifficulty] = useState('all');
  const [mcqMarks, setMcqMarks] = useState(10);
  const [extendedMarks, setExtendedMarks] = useState(20);
  const [showControls, setShowControls] = useState(false);
  const [maxMcqMarks, setMaxMcqMarks] = useState(50);
  const [maxExtendedMarks, setMaxExtendedMarks] = useState(100);
  const [availableTopics, setAvailableTopics] = useState({});
  const [topicIds, setTopicIds] = useState({});
  const [expansionState, setExpansionState] = useState({});
  const [subscriptionPromptShown, setSubscriptionPromptShown] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  const intervalRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const controlsTimeoutRef = useRef(null);

  const handleToggleExpand = useCallback((path) => {
    setExpansionState(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const getAllNodePaths = useCallback((node, currentPath = '', paths = new Set()) => {
    if (!node) return paths;
    
    if (Array.isArray(node)) {
      node.forEach(leaf => {
        paths.add(`${currentPath}/${leaf}`);
      });
    } else if (typeof node === 'object') {
      Object.keys(node).forEach(name => {
        const childPath = currentPath ? `${currentPath}/${name}` : name;
        paths.add(childPath);
        getAllNodePaths(node[name], childPath, paths);
      });
    }
    return paths;
  }, []);

  const handleFoldAll = useCallback(() => {
    setExpansionState({});
  }, []);

  const handleUnfoldAll = useCallback(() => {
    const allPaths = getAllNodePaths(availableTopics);
    const nextExpansionState = {};
    allPaths.forEach(path => { nextExpansionState[path] = true; });
    setExpansionState(nextExpansionState);
  }, [availableTopics, getAllNodePaths]);

  const handleTopicSelectionChange = useCallback((toggledPath, isSelected, descendantPaths) => {
    setSelectedTopics(prevSelected => {
      const newSelected = new Set(prevSelected);
      
      if (isSelected) {
        newSelected.add(toggledPath);
        descendantPaths.forEach(path => newSelected.add(path));

        const parts = toggledPath.split('/');
        for (let i = parts.length - 1; i > 0; i--) {
        }

      } else {
        newSelected.delete(toggledPath);
        descendantPaths.forEach(path => newSelected.delete(path));
        
        const parts = toggledPath.split('/');
        for (let i = parts.length - 1; i > 0; i--) {
            const ancestorPath = parts.slice(0, i).join('/');
            newSelected.delete(ancestorPath);
        }
      }
      
      // Track topic selection
      track('topic_selected', {
        topic: toggledPath,
        action: isSelected ? 'selected' : 'deselected',
        descendants_affected: descendantPaths.length,
        total_selected: newSelected.size,
      });
      
      return Array.from(newSelected);
    });
    
    // Update selected topic IDs
    setSelectedTopicIds(prevIds => {
      const newIds = new Set();
      const allPaths = new Set([toggledPath, ...descendantPaths]);
      
      if (isSelected) {
        // Add IDs for all selected paths
        prevIds.forEach(id => newIds.add(id));
        allPaths.forEach(path => {
          if (topicIds[path]) {
            newIds.add(topicIds[path]);
          }
        });
      } else {
        // Remove IDs for deselected paths
        prevIds.forEach(id => {
          // Find the path for this ID
          const pathForId = Object.entries(topicIds).find(([p, i]) => i === id)?.[0];
          if (!allPaths.has(pathForId)) {
            newIds.add(id);
          }
        });
      }
      
      return Array.from(newIds);
    });
  }, [track, topicIds]);

  // Show controls with animation when topics are selected
  useEffect(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (selectedTopics.length > 0 && !showControls) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(true);
      }, 300);
    } else if (selectedTopics.length === 0 && showControls) {
      setShowControls(false);
    }
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [selectedTopics, showControls]);

  // Fetch courses on component mount
  useEffect(() => {
    // Fetch subscription status for all users
    fetchSubscriptionStatus()
      .then(res => {
        setSubscriptionStatus(res.data);
        
        // Track if user needs subscription
        if (res.data && !res.data.can_generate_exam) {
          analytics.trackSubscriptionPromptShown('exam_generation_blocked', 'generate_exam', {
            has_active_subscription: res.data.has_active_subscription,
            allowance_remaining: res.data.allowance_remaining,
            is_alpha_tester: res.data.message === "Alpha tester with extended trial privileges",
            reason: res.data.message
          });
        }
      })
      .catch(err => console.error('Failed to fetch subscription status:', err));

    fetchCourses()
      .then(res => {
        setCourses(res.data);
        // Select first course by default if available
        if (res.data.length > 0 && res.data[0]?.id) {
          setSelectedCourse(res.data[0].id);
        } else {
          setAvailableTopics({});
        }
      })
      .catch(err => {
        console.error('Failed to fetch courses:', err);
        setError('Failed to load courses. Please refresh the page.');
      });
  }, [user]);

  // Fetch available topics when course changes
  useEffect(() => {
    if (selectedCourse) {
      fetchAvailableTopics(selectedCourse)
        .then(res => {
          // Handle both old format (direct topics) and new format (topics + topic_ids)
          if (res.data.topics && res.data.topic_ids) {
            setAvailableTopics(res.data.topics);
            setTopicIds(res.data.topic_ids);
          } else {
            // Backward compatibility - old API format
            setAvailableTopics(res.data);
            setTopicIds({});
          }
          setSelectedTopics([]); // Reset selection when course changes
          setSelectedTopicIds([]); // Reset selected IDs
          setExpansionState({}); // Reset expansion state
        })
        .catch(err => {
          console.error('Failed to fetch topics:', err);
          setError('Failed to load topics. Please try again.');
        });
    }
  }, [selectedCourse]);

  // Fetch available marks when topics or difficulty change
  useEffect(() => {
    const updateAvailableMarks = async () => {
      if (selectedTopics.length === 0) {
        setMaxMcqMarks(0);
        setMaxExtendedMarks(0);
        setMcqMarks(0);
        setExtendedMarks(0);
        return;
      }
      
      try {
        const response = await fetchAvailableMarks(selectedTopics, difficulty, selectedCourse, selectedTopicIds);
        const { mcq, extended } = response.data;
        setMaxMcqMarks(mcq);
        setMaxExtendedMarks(extended);
        
        if (maxMcqMarks === 0 && maxExtendedMarks === 0) {
          setMcqMarks(Math.min(10, mcq));
          setExtendedMarks(Math.min(20, extended));
        } else {
          setMcqMarks(current => Math.min(current, mcq));
          setExtendedMarks(current => Math.min(current, extended));
        }
      } catch (err) {
        console.error('Failed to fetch available marks:', err);
        setError('Failed to update available marks based on selection.');
      }
    };

    updateAvailableMarks();

  }, [selectedTopics, difficulty, selectedCourse]);

  const handleDifficultyChange = (event, newDifficulty) => {
    if (newDifficulty !== null) {
      setDifficulty(newDifficulty);
      track('difficulty_changed', {
        difficulty: newDifficulty,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedTopics.length === 0) {
      setError("Please select at least one topic.");
      return;
    }
    if (mcqMarks === 0 && extendedMarks === 0) {
       setError("Please allocate marks for MCQ or Extended Response questions.");
       return;
     }
     
    // Check if user hit exam limit
    if (subscriptionStatus && !subscriptionStatus.can_generate_exam) {
      analytics.trackExamLimitReached(
        subscriptionStatus.allowance_used || 0,
        subscriptionStatus.exam_allowance || 0,
        {
          has_active_subscription: subscriptionStatus.has_active_subscription,
          is_alpha_tester: subscriptionStatus.message === "Alpha tester with extended trial privileges",
          subscription_status: subscriptionStatus.subscription_status
        }
      );
      setError("You have reached your exam generation limit. Please subscribe to continue.");
      return;
    }
    
    // Check subscription status
    if (subscriptionStatus && !subscriptionStatus.can_generate_exam) {
      if (!subscriptionStatus.has_active_subscription && !subscriptionStatus.in_grace_period) {
        setError("You need an active subscription to generate exams. Please subscribe to continue.");
        return;
      } else if (subscriptionStatus.allowance_remaining === 0) {
        setError("You have reached your exam generation limit for this period. Please upgrade your plan or wait for the next billing period.");
        return;
      }
    }

    try {
      setGenerating(true);
      setError(null);
      setTaskStatus(null);
      setTaskId(null);
      setDownloadTriggered(false); // Reset download flag for new generation
      downloadTriggeredRef.current = false; // Reset ref as well

      const params = {
        topics_include: selectedTopics,
        topic_ids: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
        difficulty_range: difficulty,
        mcq_marks: mcqMarks,
        extended_marks: extendedMarks,
        course_id: selectedCourse,
      };
      
      // Track exam generation start
      trackExamGeneration('started', {
        topics_count: selectedTopics.length,
        difficulty_min: difficulty[0],
        difficulty_max: difficulty[1],
        mcq_marks: mcqMarks,
        extended_marks: extendedMarks,
        total_marks: mcqMarks + extendedMarks,
        is_trial: user?.role === 'STUDENT' && !user?.subscription_status,
        trial_exams_used: user?.trial_exams_used || 0,
      });
      
      const response = await generateExam(params);
      setTaskId(response.data.task_id);
      setTaskStatus({
         task_id: response.data.task_id,
         status: 'queued',
         progress: 0.0,
         message: 'Task queued'
       });

      // Store task ID in ref to avoid closure issues
      const currentTaskId = response.data.task_id;
      
      // Initial status check
      checkStatus(currentTaskId);
      
      // Create polling function that uses the ref
      const pollStatus = () => {
        if (intervalRef.current) {
          checkStatus(currentTaskId);
        }
      };
      
      const interval = setInterval(pollStatus, 500); // Poll every 0.5 seconds
      
      intervalRef.current = interval;
      
      // Test that setInterval is working
      setTimeout(() => {
        console.log('Test: 1 second passed, interval should still be running:', intervalRef.current);
      }, 1000);
      
    } catch (err) {
      console.error('Failed to start exam generation:', err);
      const errorDetail = err.response?.data?.detail || 'Please check API connectivity.';
      setError(`Failed to start exam generation: ${errorDetail}`);
      setGenerating(false);
       if (intervalRef.current) {
         clearInterval(intervalRef.current);
         intervalRef.current = null;
       }
       
       // Track generation error
       trackError(err, {
         context: 'exam_generation_start',
         error_detail: errorDetail,
       });
    }
  };
  
  const checkStatus = async (id) => {
    if (!id) return;
    
    try {
      const response = await checkGenerationStatus(id);
       if (taskStatus?.status === 'queued' && response.data.status === 'queued') {
         return; 
       }
      setTaskStatus(response.data);
      
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        // Clear interval immediately using ref
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setGenerating(false);
         if (response.data.status === 'failed') {
           setError(`Exam generation failed: ${response.data.message || 'Unknown error'}`);
           setDownloadTriggered(false); // Reset on failure
           downloadTriggeredRef.current = false;
           
           // Track failed generation
           trackExamGeneration('failed', {
             task_id: id,
             error_message: response.data.message,
           });
         } else if (response.data.status === 'completed' && !downloadTriggeredRef.current) {
           // Track successful generation
           trackExamGeneration('completed', {
             task_id: id,
           });
           
           // Auto-download the exam when generation is complete (only once)
           downloadTriggeredRef.current = true;
           setDownloadTriggered(true);
           handleDownload(id);
           
           // Refresh subscription status after exam generation
           // Add a small delay to ensure backend has recorded the usage
           setTimeout(() => {
             fetchSubscriptionStatus()
               .then(res => {
                 setSubscriptionStatus(res.data);
                 console.log('Subscription status refreshed:', res.data);
               })
               .catch(err => console.error('Failed to refresh subscription status:', err));
           }, 1000);
         }
      } else {
         setGenerating(true); 
       }
    } catch (err) {
      console.error('Failed to check task status:', err);
      // Clear both ref and state
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setGenerating(false);
      setError('Failed to check task status. Please check progress manually or try again.');
    }
  };
  
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount
  
  const handleDownload = async (examId) => {
    try {
      const downloadId = examId || taskId;
      if (!downloadId) {
        setError('No exam ID available for download');
        return;
      }
      
      const response = await downloadExam(downloadId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'exam';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      } else {
           const ext = response.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
           filename = `exam_${downloadId.substring(0, 8)}${ext}`;
       }
      
      if (!/\.(pdf|tex)$/i.test(filename)) {
         const ext = response.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
         filename += ext;
       }

      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      // Track successful download
      track('exam_downloaded', {
        exam_id: downloadId,
        filename: filename,
        file_type: filename.endsWith('.pdf') ? 'pdf' : 'tex',
      });
      
    } catch (err) {
      console.error('Failed to download exam:', err);
      const errorDetail = err.response?.data?.detail || 'Please try again later.';
      setError(`Failed to download exam: ${errorDetail}`);
    }
  };

  const getActiveStep = () => {
    if (!taskStatus) return -1;
    
    switch (taskStatus.status) {
      case 'queued':
        return 0;
      case 'running':
         return 1; 
      case 'completed':
        return 3;
      case 'failed':
        return -1;
      default:
        return -1;
    }
  };
  
  const estimatedDuration = Math.ceil((mcqMarks + extendedMarks) / 1.2);

  // Calculate current step based on user progress
  const getCurrentStep = () => {
    if (!selectedCourse) return 0;
    if (selectedTopics.length === 0) return 1;
    if (!showControls) return 1;
    if (mcqMarks === 0 && extendedMarks === 0) return 2;
    return 3;
  };

  return (
    <div>
      {/* Progress Steps */}
      <Fade in={true} timeout={300}>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {[
              { label: 'Select Course', icon: '1' },
              { label: 'Choose Topics', icon: '2' },
              { label: 'Set Difficulty', icon: '3' },
              { label: 'Allocate Marks', icon: '4' }
            ].map((step, index) => {
              const isActive = getCurrentStep() >= index;
              const isComplete = getCurrentStep() > index;
              
              return (
                <React.Fragment key={step.label}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    opacity: isActive ? 1 : 0.5,
                    transition: 'all 0.3s ease'
                  }}>
                    <Fade in={isActive} timeout={200 + (index * 100)}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: isComplete ? 'primary.main' : isActive ? 'primary.light' : 'action.disabled',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}>
                        {isComplete ? '✓' : step.icon}
                      </Box>
                    </Fade>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        ml: 1,
                        fontWeight: isActive ? 500 : 400,
                        color: isActive ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                  {index < 3 && (
                    <Box sx={{ 
                      flex: 1, 
                      height: 2, 
                      backgroundColor: isComplete ? 'primary.main' : 'action.disabled',
                      mx: 2,
                      transition: 'all 0.3s ease'
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      </Fade>
      
      {/* Subscription status indicator */}
      {subscriptionStatus && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            {subscriptionStatus.has_active_subscription ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Exams Used This Period
                  </Typography>
                  <Chip 
                    label={subscriptionStatus.exam_allowance === null ? 
                      `${subscriptionStatus.allowance_used} / Unlimited` : 
                      `${subscriptionStatus.allowance_used} / ${subscriptionStatus.exam_allowance}`
                    } 
                    size="small"
                    sx={{
                      backgroundColor: theme => alpha(theme.palette.success.main, 0.15),
                      color: 'success.dark'
                    }}
                  />
                </Box>
                {subscriptionStatus.exam_allowance !== null && (
                  <LinearProgress 
                    variant="determinate" 
                    value={(subscriptionStatus.allowance_used / subscriptionStatus.exam_allowance) * 100} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: alpha(theme.palette.success.main, 0.7)
                      }
                    }}
                  />
                )}
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  {subscriptionStatus.exam_allowance === null ? 
                    'Unlimited exam generation' :
                    subscriptionStatus.allowance_remaining === 0 ?
                      'You have reached your exam limit for this period.' :
                      `${subscriptionStatus.allowance_remaining} exam${subscriptionStatus.allowance_remaining !== 1 ? 's' : ''} remaining this period`
                  }
                </Typography>
              </>
            ) : (subscriptionStatus.message === "Trial user with limited exams" || subscriptionStatus.message === "Alpha tester with extended trial privileges" || subscriptionStatus.in_grace_period) ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {subscriptionStatus.message === "Alpha tester with extended trial privileges" ? "Credits Used" :
                     subscriptionStatus.message === "Trial user with limited exams" ? "Trial Exams" : "Grace Period - Trial Exams"}
                  </Typography>
                  <Chip 
                    label={`${subscriptionStatus.allowance_used} / ${subscriptionStatus.exam_allowance}`} 
                    size="small"
                    sx={{
                      backgroundColor: theme => alpha(theme.palette.success.main, 0.15),
                      color: 'success.dark'
                    }}
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(subscriptionStatus.allowance_used / subscriptionStatus.exam_allowance) * 100} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: theme => alpha(theme.palette.success.main, 0.2),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme => alpha(theme.palette.success.main, 0.7)
                    }
                  }}
                />
                {subscriptionStatus.message !== "Alpha tester with extended trial privileges" && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    {subscriptionStatus.message === "Trial user with limited exams" ? 
                      `${subscriptionStatus.allowance_remaining} trial exam${subscriptionStatus.allowance_remaining !== 1 ? 's' : ''} remaining.` :
                      `${subscriptionStatus.days_until_grace_expires} days remaining in grace period. ${subscriptionStatus.allowance_remaining} trial exam${subscriptionStatus.allowance_remaining !== 1 ? 's' : ''} remaining.`
                    }
                  </Typography>
                )}
              </>
            ) : (
              <Alert severity="info">
                No active subscription. <SubscribeButton size="small" buttonText="Subscribe now" variant="text" /> to generate exams.
              </Alert>
            )}
          </Box>
        </Card>
      )}
      
      {/* Subscription warnings */}
      {subscriptionStatus && (
        <>
          {!subscriptionStatus.can_generate_exam && !subscriptionStatus.has_active_subscription && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {subscriptionStatus.message === "Trial exam limit reached - please subscribe to continue" ? 
                "You've used all your trial exams." : 
                "You need an active subscription to generate exams."
              }{' '}
              <SubscribeButton size="small" variant="outlined" sx={{ ml: 1 }} />
            </Alert>
          )}
          {subscriptionStatus.can_generate_exam && subscriptionStatus.message === "Trial user with limited exams" && subscriptionStatus.allowance_remaining > 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You're using a free trial. <SubscribeButton size="small" variant="outlined" buttonText="Subscribe for more exams" sx={{ ml: 1 }} />
            </Alert>
          )}
        </>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      
      {/* Course Selection */}
      <Card sx={{ mb: 3, position: 'relative', overflow: 'visible' }}>
        <Box sx={{ 
          position: 'absolute', 
          top: 16, 
          left: 16,
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: 'white',
          color: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 500,
          fontSize: '1.1rem',
          zIndex: 1,
          border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          1
        </Box>
        <Box sx={{ p: 3, pl: 7 }}>
          <FormControl fullWidth>
            <InputLabel>Course</InputLabel>
            <Select
              value={selectedCourse || ''}
              onChange={(e) => setSelectedCourse(e.target.value)}
              label="Course"
              disabled={generating}
            >
              {courses.map(course => (
                <MenuItem key={course.id} value={course.id}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>
      
      {/* Main exam generation form */}
      {selectedCourse && (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left side - Topic selection */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '600px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TopicTree 
                  topics={availableTopics} 
                  selectedTopics={selectedTopics} 
                  onSelectionChange={handleTopicSelectionChange} 
                  expansionState={expansionState}
                  onToggleExpand={handleToggleExpand}
                  onFoldAll={handleFoldAll}
                  onUnfoldAll={handleUnfoldAll}
                />
              </Box>
            </Grid>
            
            {/* Right side - Exam parameters */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '600px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Fade in={showControls} timeout={500}>
                  <Paper elevation={2} sx={{ 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 16, 
                      left: 16,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      color: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      zIndex: 1,
                      border: theme => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}>
                      3
                    </Box>
                    
                    <Box sx={{ mb: 3, mt: 2 }}>
                      <Typography variant="body1" gutterBottom sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                        Difficulty
                      </Typography>
                      <ToggleButtonGroup
                        value={difficulty}
                        exclusive
                        onChange={handleDifficultyChange}
                        aria-label="difficulty"
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              }
                            }
                          }
                        }}
                      >
                        <ToggleButton value="all" disabled={generating}>
                          Mixed/All
                        </ToggleButton>
                        <ToggleButton value="easy" disabled={generating}>
                          Easy
                        </ToggleButton>
                        <ToggleButton value="medium" disabled={generating}>
                          Medium
                        </ToggleButton>
                        <ToggleButton value="hard" disabled={generating}>
                          Hard
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                        Marks
                      </Typography>
                      
                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Typography sx={{ flex: 1 }}>
                            Multiple Choice Questions
                          </Typography>
                          <TextField
                            value={mcqMarks}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setMcqMarks(Math.min(Math.max(0, value), maxMcqMarks));
                            }}
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: maxMcqMarks }}
                            sx={{ width: 80 }}
                            disabled={generating || maxMcqMarks === 0}
                          />
                          <Typography variant="body2" color="text.secondary">
                            / {maxMcqMarks}
                          </Typography>
                        </Box>
                        <Slider
                          value={mcqMarks}
                          onChange={(e, newValue) => {
                            setMcqMarks(newValue);
                            track('marks_adjusted', {
                              type: 'mcq',
                              value: newValue,
                              max: maxMcqMarks,
                            });
                          }}
                          aria-labelledby="mcq-marks-slider"
                          valueLabelDisplay="auto"
                          step={1}
                          marks
                          min={0}
                          max={maxMcqMarks}
                          disabled={generating || maxMcqMarks === 0}
                          sx={{
                            '& .MuiSlider-thumb': {
                              transition: 'all 0.2s ease-out',
                              '&:hover': {
                                boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)'
                              }
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, mt: 3 }}>
                          <Typography sx={{ flex: 1 }}>
                            Extended Response Questions
                          </Typography>
                          <TextField
                            value={extendedMarks}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setExtendedMarks(Math.min(Math.max(0, value), maxExtendedMarks));
                            }}
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: maxExtendedMarks }}
                            sx={{ width: 80 }}
                            disabled={generating || maxExtendedMarks === 0}
                          />
                          <Typography variant="body2" color="text.secondary">
                            / {maxExtendedMarks}
                          </Typography>
                        </Box>
                        <Slider
                          value={extendedMarks}
                          onChange={(e, newValue) => {
                            setExtendedMarks(newValue);
                            track('marks_adjusted', {
                              type: 'extended',
                              value: newValue,
                              max: maxExtendedMarks,
                            });
                          }}
                          aria-labelledby="extended-marks-slider"
                          valueLabelDisplay="auto"
                          step={1}
                          marks
                          min={0}
                          max={maxExtendedMarks}
                          disabled={generating || maxExtendedMarks === 0}
                          sx={{
                            '& .MuiSlider-thumb': {
                              transition: 'all 0.2s ease-out',
                              '&:hover': {
                                boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)'
                              }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      backgroundColor: 'grey.50',
                      p: 2,
                      borderRadius: 1
                    }}>
                      <Grow in={mcqMarks > 0 || extendedMarks > 0}>
                        <Box>
                          <Typography variant="h6" color="primary">
                            Total: {mcqMarks + extendedMarks} marks
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Est. duration: {estimatedDuration} minutes
                            <Typography variant="caption" sx={{ml: 1}}>(approx. 1.2 marks/min)</Typography>
                          </Typography>
                        </Box>
                      </Grow>
                    </Box>
                  </Paper>
                </Fade>
              </Box>
            </Grid>
            
            {/* Full width generate button section */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                {/* Show subscription button when user can't generate, on last exam, or alpha tester with 1+ exams used */}
                {subscriptionStatus && (!subscriptionStatus.can_generate_exam || 
                  (subscriptionStatus.allowance_remaining === 1 && (subscriptionStatus.message === "Trial user with limited exams" || subscriptionStatus.message === "Alpha tester with extended trial privileges")) ||
                  (subscriptionStatus.message === "Alpha tester with extended trial privileges" && subscriptionStatus.allowance_used >= 1)) && (
                  <Box sx={{ mb: 2 }}>
                    {(() => {
                      // Track subscription prompt shown only once
                      if (!subscriptionPromptShown) {
                        setSubscriptionPromptShown(true);
                        const promptType = subscriptionStatus.allowance_remaining === 1 ? 'last_exam_warning' : 'exam_limit_reached';
                        analytics.trackSubscriptionPromptShown(promptType, 'generate_exam_page', {
                          allowance_remaining: subscriptionStatus.allowance_remaining,
                          exam_allowance: subscriptionStatus.exam_allowance,
                          is_alpha_tester: subscriptionStatus.message === "Alpha tester with extended trial privileges",
                          has_active_subscription: subscriptionStatus.has_active_subscription
                        });
                      }
                      return null;
                    })()}
                    {subscriptionStatus.message === "Alpha tester with extended trial privileges" ? (
                      <SubscribeButtonWithCoupon 
                        size="large"
                        variant="contained"
                        fullWidth
                        isAlphaTester={true}
                        showCouponStatus={true}
                        sx={{
                          backgroundColor: theme => alpha(theme.palette.success.main, 0.85),
                          '&:hover': {
                            backgroundColor: 'success.main'
                          }
                        }}
                      />
                    ) : (
                      <SubscribeButton 
                        size="large"
                        variant="contained"
                        fullWidth
                        buttonText="Subscribe to Generate Exams"
                        sx={{
                          backgroundColor: theme => alpha(theme.palette.success.main, 0.85),
                          '&:hover': {
                            backgroundColor: 'success.main'
                          }
                        }}
                      />
                    )}
                  </Box>
                )}
                
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={generating || (mcqMarks === 0 && extendedMarks === 0) || selectedTopics.length === 0 || (subscriptionStatus && !subscriptionStatus.can_generate_exam)}
                    fullWidth
                    onClick={(e) => {
                      // Track which button was clicked
                      track('exam_generation_button_clicked', {
                        button_type: 'view_online',
                        topics_count: selectedTopics.length,
                        total_marks: mcqMarks + extendedMarks
                      });
                      handleSubmit(e);
                    }}
                    startIcon={!generating && <VisibilityIcon />}
                  >
                    {generating ? 'Generating Exam...' : 
                    (subscriptionStatus && !subscriptionStatus.can_generate_exam) ? 'Subscription Required' : 
                    'Generate & View'}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={generating || (mcqMarks === 0 && extendedMarks === 0) || selectedTopics.length === 0 || (subscriptionStatus && !subscriptionStatus.can_generate_exam)}
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      // Track which button was clicked
                      track('exam_generation_button_clicked', {
                        button_type: 'download_pdf',
                        topics_count: selectedTopics.length,
                        total_marks: mcqMarks + extendedMarks
                      });
                      handleSubmit(e);
                    }}
                    startIcon={!generating && <DownloadIcon />}
                  >
                    {generating ? 'Generating PDF...' : 
                    (subscriptionStatus && !subscriptionStatus.can_generate_exam) ? 'Subscription Required' : 
                    'Download PDF'}
                  </Button>
                </Stack>
              </Box>
            </Grid>
            
          </Grid>
        </form>
      )}
      
      {/* Alpha Referral Card for founders - moved below form */}
      {!taskId && <AlphaReferralCardCompact subscriptionStatus={subscriptionStatus} />}
      
      {/* Generation Status Overlay - Shows at the top with greyed out background */}
      {taskId && (taskStatus?.status === 'running' || taskStatus?.status === 'queued') && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 8
          }}
        >
          <Fade in={true} timeout={500}>
            <Paper sx={{ 
              p: 4, 
              textAlign: 'center', 
              position: 'relative', 
              overflow: 'hidden',
              minWidth: 400,
              maxWidth: 600,
              mx: 2
            }}>
              <Box>
                {/* Animated background effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '200%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.1), transparent)',
                    animation: 'shimmer 2s infinite',
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(50%)' }
                    }
                  }}
                />
                
                {/* Main content */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {/* Multiple animated elements */}
                  <Box sx={{ mb: 3, position: 'relative', height: 120 }}>
                    {/* Central spinning circle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        border: '3px solid',
                        borderColor: 'primary.light',
                        borderTopColor: 'primary.main',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                          '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                        }
                      }}
                    />
                    
                    {/* Orbiting dots */}
                    {[0, 1, 2].map((index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 100,
                          height: 100,
                          animation: `orbit 3s linear infinite`,
                          animationDelay: `${index * 1}s`,
                          '@keyframes orbit': {
                            '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                            '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            opacity: 0.6,
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${index * 0.5}s`,
                            '@keyframes pulse': {
                              '0%, 100%': { transform: 'translateX(-50%) scale(1)', opacity: 0.6 },
                              '50%': { transform: 'translateX(-50%) scale(1.5)', opacity: 1 }
                            }
                          }}
                        />
                      </Box>
                    ))}
                    
                    {/* Pulsing ring */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        opacity: 0,
                        animation: 'ripple 2s ease-out infinite',
                        '@keyframes ripple': {
                          '0%': {
                            transform: 'translate(-50%, -50%) scale(1)',
                            opacity: 0.8
                          },
                          '100%': {
                            transform: 'translate(-50%, -50%) scale(2)',
                            opacity: 0
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Typography variant="h6" color="primary" gutterBottom>
                    Generating Your Exam
                  </Typography>
                  
                  {/* Animated text with cycling messages */}
                  <Box sx={{ height: 24, mb: 2 }}>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{
                        animation: 'fadeInOut 3s infinite',
                        '@keyframes fadeInOut': {
                          '0%, 100%': { opacity: 0 },
                          '10%, 90%': { opacity: 1 }
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          '&::before': {
                            content: '"Analyzing topics..."',
                            animation: 'textContent 15s infinite step-end',
                            '@keyframes textContent': {
                              '0%': { content: '"Analyzing topics..."' },
                              '20%': { content: '"Finding MCQ questions..."' },
                              '40%': { content: '"Getting extended questions..."' },
                              '60%': { content: '"Checking difficulty levels..."' },
                              '80%': { content: '"Formatting your exam..."' }
                            }
                          }
                        }}
                      />
                    </Typography>
                  </Box>
                  
                  {/* Fancy animated progress indicator */}
                  <Box sx={{ mt: 3, mb: 2 }}>
                    {/* Wave animation */}
                    <Box
                      sx={{
                        height: 40,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5
                      }}
                    >
                      {[...Array(12)].map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: 4,
                            height: 20,
                            backgroundColor: 'primary.main',
                            borderRadius: 2,
                            animation: 'wave 1.2s ease-in-out infinite',
                            animationDelay: `${index * 0.1}s`,
                            '@keyframes wave': {
                              '0%, 100%': {
                                height: 20,
                                opacity: 0.3
                              },
                              '50%': {
                                height: 40,
                                opacity: 1
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                    
                    {/* Alternative: DNA helix animation */}
                    <Box
                      sx={{
                        mt: 2,
                        height: 6,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3,
                        backgroundColor: 'action.hover'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '100%',
                          background: `repeating-linear-gradient(
                            90deg,
                            #1976d2 0px,
                            #1976d2 20px,
                            #42a5f5 20px,
                            #42a5f5 40px
                          )`,
                          animation: 'slide 1s linear infinite',
                          '@keyframes slide': {
                            '0%': { transform: 'translateX(0)' },
                            '100%': { transform: 'translateX(40px)' }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Floating elements animation */}
                  <Box sx={{ position: 'relative', height: 30, overflow: 'hidden' }}>
                    {['MCQ', '?', '✓', 'Q'].map((char, index) => (
                      <Typography
                        key={index}
                        sx={{
                          position: 'absolute',
                          fontSize: 14,
                          color: 'primary.light',
                          opacity: 0.6,
                          animation: `float ${3 + index}s ease-in-out infinite`,
                          animationDelay: `${index * 0.7}s`,
                          left: `${20 + index * 20}%`,
                          '@keyframes float': {
                            '0%, 100%': {
                              transform: 'translateY(30px)',
                              opacity: 0
                            },
                            '50%': {
                              transform: 'translateY(-10px)',
                              opacity: 0.6
                            }
                          }
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                  </Box>
                  
                  {taskStatus?.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {taskStatus.message}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>
      )}
      
      {/* Success Status - Shows after generation completes */}
      {taskId && taskStatus?.status === 'completed' && (
        <Fade in={true} timeout={500}>
          <Paper sx={{ p: 4, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <Zoom in={true} timeout={500}>
                <Box>
                  {/* Success checkmark */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        margin: '0 auto',
                        borderRadius: '50%',
                        backgroundColor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'scaleIn 0.5s ease-out',
                        '@keyframes scaleIn': {
                          '0%': { transform: 'scale(0)' },
                          '50%': { transform: 'scale(1.1)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: 'white' }}>✓</Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Exam Generated Successfully!
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    onClick={() => handleDownload(taskId)}
                    disabled={!taskId}
                    sx={{ mt: 2 }}
                  >
                    Download Exam
                  </Button>
              </Box>
            </Zoom>
          </Paper>
        </Fade>
      )}
      
      {/* Failed Status - Shows if generation fails */}
      {taskId && taskStatus?.status === 'failed' && (
        <Fade in={true} timeout={500}>
          <Paper sx={{ p: 4, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <Zoom in={true} timeout={500}>
                <Box>
                  {/* Error icon */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        margin: '0 auto',
                        borderRadius: '50%',
                        backgroundColor: 'error.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'shake 0.5s ease-out',
                        '@keyframes shake': {
                          '0%, 100%': { transform: 'translateX(0)' },
                          '25%': { transform: 'translateX(-10px)' },
                          '75%': { transform: 'translateX(10px)' }
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: 'white' }}>✕</Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" color="error" gutterBottom>
                    Generation Failed
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {taskStatus?.message || 'An error occurred during generation.'}
                  </Typography>
              </Box>
            </Zoom>
          </Paper>
        </Fade>
      )}
    </div>
  );
};

export default GenerateExam;
